import { useState, useEffect, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Server, Plus, Trash2, Copy, Radio, Loader2, Cpu, Clock } from "lucide-react";

export default function ProxyNodes({ isAdmin }) {
  const [nodes, setNodes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pool, setPool] = useState([]);
  const [nodeName, setNodeName] = useState("");
  const [form, setForm] = useState({ node_id: "", pool_id: "", buyer_ip: "", days: 30 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [n, o, p] = await Promise.all([api.get("/nodes"), api.get("/orders"), api.get("/pool")]);
      setNodes(n.data || []);
      setOrders(o.data || []);
      setPool(p.data || []);
    } catch (e) {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  if (!isAdmin) return null;

  const installCmd = (n) =>
    `curl -fsSLk "${window.location.origin}/api/agent/install.sh?id=${n.id}&secret=${n.secret}" | sudo bash`;

  const addNode = async () => {
    if (!nodeName.trim()) return toast.error("Enter a node name");
    try {
      await api.post("/nodes", { name: nodeName.trim() });
      setNodeName("");
      toast.success("Node created — copy the install command to the device");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const delNode = async (id) => {
    try {
      await api.delete(`/nodes/${id}`);
      toast.success("Node deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createOrder = async () => {
    if (!form.node_id || !form.pool_id || !form.buyer_ip.trim()) return toast.error("Fill in node, proxy IP & buyer IP");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...form, buyer_ip: form.buyer_ip.trim(), days: Number(form.days) || 30 });
      toast.success(`Order created · port ${data.port} for ${data.buyer_ip}`);
      setForm({ node_id: "", pool_id: "", buyer_ip: "", days: 30 });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Order revoked");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const copy = (txt, msg) => {
    navigator.clipboard?.writeText(txt);
    toast.success(msg || "Copied");
  };

  return (
    <div className="card-panel overflow-hidden" data-testid="nodes-panel">
      <div className="flex items-center gap-2 p-4 border-b border-line bg-accent/30">
        <Server size={16} className="text-neon" />
        <div>
          <h3 className="font-heading text-base tracking-tight leading-none">Proxy Nodes · Your Own Devices</h3>
          <span className="overline">agent on WiFi device · IP whitelist · direct traffic (zero VPS load)</span>
        </div>
      </div>

      {/* Nodes */}
      <div className="p-4 border-b border-line">
        <div className="flex items-center gap-2 mb-3">
          <Cpu size={13} className="text-dim" />
          <span className="overline">Devices / Nodes</span>
        </div>
        <div className="flex gap-2 mb-3">
          <Input
            value={nodeName}
            onChange={(e) => setNodeName(e.target.value)}
            placeholder="Node name (e.g. RPi-Home-1)"
            className="h-9 bg-black border-line font-mono text-xs rounded-md"
            data-testid="node-name-input"
          />
          <Button onClick={addNode} size="sm" className="h-9 rounded-md bg-neon text-white hover:bg-neon/90 font-mono text-xs gap-1.5" data-testid="node-create-btn">
            <Plus size={14} /> Add Node
          </Button>
        </div>
        {nodes.length === 0 ? (
          <p className="text-xs text-dim font-mono">No nodes yet. Add a node then install the agent on the device.</p>
        ) : (
          <div className="space-y-2">
            {nodes.map((n) => (
              <div key={n.id} className="border border-line rounded-md p-3" data-testid={`node-row-${n.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Radio size={13} className={n.online ? "text-laser" : "text-dim"} />
                    <span className="font-medium text-sm">{n.name}</span>
                    <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${n.online ? "bg-laser/15 text-laser" : "bg-muted text-dim"}`}>
                      {n.online ? "ONLINE" : "OFFLINE"}
                    </span>
                    <span className="font-mono text-[11px] text-dim">{n.public_host || "IP not detected"} · {n.active_orders || 0} active orders</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => copy(installCmd(n), "Install command copied")} size="sm" variant="outline" className="h-7 rounded-md border-line hover:border-neon hover:text-neon font-mono text-[11px] gap-1" data-testid={`node-install-${n.id}`}>
                      <Copy size={12} /> Install Command
                    </Button>
                    <button onClick={() => delNode(n.id)} className="text-dim hover:text-signal transition-colors" title="Delete node" data-testid={`node-delete-${n.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <code className="block mt-2 bg-black border border-line rounded px-2 py-1.5 font-mono text-[10px] text-dim overflow-x-auto whitespace-nowrap">
                  {installCmd(n)}
                </code>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create order */}
      <div className="p-4 border-b border-line">
        <span className="overline block mb-3">Create Rental Order (assign IP → buyer)</span>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select value={form.node_id} onChange={(e) => setForm({ ...form, node_id: e.target.value })} className="h-9 rounded-md border border-line bg-black px-2 font-mono text-xs text-foreground focus:outline-none focus:border-neon" data-testid="order-node-select">
            <option value="">— select node —</option>
            {nodes.map((n) => <option key={n.id} value={n.id}>{n.name} {n.online ? "🟢" : "⚫"}</option>)}
          </select>
          <select value={form.pool_id} onChange={(e) => setForm({ ...form, pool_id: e.target.value })} className="h-9 rounded-md border border-line bg-black px-2 font-mono text-xs text-foreground focus:outline-none focus:border-neon" data-testid="order-ip-select">
            <option value="">— select proxy IP —</option>
            {pool.map((p) => <option key={p.id} value={p.id}>{p.ip} · {p.country} {p.city || ""} · {p.isp || "?"}</option>)}
          </select>
          <Input value={form.buyer_ip} onChange={(e) => setForm({ ...form, buyer_ip: e.target.value })} placeholder="Buyer public IP" className="h-9 bg-black border-line font-mono text-xs rounded-md" data-testid="order-buyer-ip" />
          <div className="flex gap-2">
            <Input type="number" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} placeholder="Days" className="h-9 w-20 bg-black border-line font-mono text-xs rounded-md" data-testid="order-days" />
            <Button onClick={createOrder} disabled={busy} size="sm" className="h-9 flex-1 rounded-md bg-neon text-white hover:bg-neon/90 font-mono text-xs gap-1.5" data-testid="order-create-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
            </Button>
          </div>
        </div>
      </div>

      {/* Orders */}
      <div className="overflow-x-auto">
        {orders.length === 0 ? (
          <p className="p-6 text-center text-sm text-dim font-mono">No rental orders yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-4 py-2 overline font-normal">Endpoint (SOCKS5)</th>
                <th className="px-4 py-2 overline font-normal">Buyer IP</th>
                <th className="px-4 py-2 overline font-normal">Exit / Location</th>
                <th className="px-4 py-2 overline font-normal">Expires</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const endpoint = o.host ? `${o.host}:${o.port}` : `(node offline):${o.port}`;
                return (
                  <tr key={o.id} className="border-b border-line/60 hover:bg-neon/5" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <code className={`font-mono text-sm font-semibold ${o.active ? "text-neon" : "text-dim line-through"}`} data-testid={`order-endpoint-${o.id}`}>{endpoint}</code>
                        {o.host && <button onClick={() => copy(endpoint, "Endpoint copied")} className="text-dim hover:text-neon" data-testid={`order-copy-${o.id}`}><Copy size={12} /></button>}
                      </div>
                      <span className="font-mono text-[10px] text-dim">{o.node_name} · socks5 · no-auth</span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{o.buyer_ip}</td>
                    <td className="px-4 py-2.5 text-xs">{o.exit_ip}<div className="text-dim text-[11px]">{o.country} {o.city} · {o.isp}</div></td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 font-mono text-[11px] ${o.active ? "text-laser" : "text-signal"}`}>
                        <Clock size={11} /> {String(o.expires_at).slice(0, 10)} {o.active ? "" : "(expired)"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => revoke(o.id)} className="text-dim hover:text-signal transition-colors" title="Revoke" data-testid={`order-revoke-${o.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-4 py-2.5 border-t border-line bg-accent/20">
        <span className="font-mono text-[10px] text-dim">
          Buyer connects: <b>SOCKS5</b> to <b>node-IP:port</b> (no user/pass). Only the whitelisted buyer IP can access (all others are DROPped by the firewall). Traffic: buyer → node → FloppyData → target (the VPS is bypassed).
        </span>
      </div>
    </div>
  );
}
